import { useNavigate } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import {
  MDSDivider,
  MDSDropdown,
  MDSGNB,
  MDSIcon,
  MDSImage,
  MDSPlainButton,
  MDSTooltip,
  MDSTypography,
} from '@marqvision/mds-v2';
import { CountryCodeEnum, getFlagEmoji, UseNavigationProps } from '@marqvision/shared';
import { useBrandFilterMode, useIsPiracy } from '~/hooks';
import { RootRouteEnum, SettingsRouteEnum } from '~/router/@types';
import { MarqCommonEnumsClientClientRoleEnum } from '~/swagger/data-contracts';
import { ALL_BRANDS_LABEL } from './@constants';
import { useViewModel } from './@hooks';
import { ReleaseNote } from './ReleaseNote';

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '16px',
    width: '100%',
  },
  utilityContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    marginInlineStart: 'auto',
  },
  countryBox: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  countryCountLabel: {
    borderBottom: `1px dashed ${theme._raw_color.bluegray400}`,
    cursor: 'pointer',
  },
}));

type Props = {
  isAdmin?: boolean;
} & UseNavigationProps['gnbProps'];

export const GlobalNavigationBar = (props: Props) => {
  const { isAdmin, ...restProps } = props;
  const classes = useStyles();
  const navigate = useNavigate();
  const { brandFilterMode } = useBrandFilterMode();

  const { isPiracy } = useIsPiracy();
  const { client, brand, country, user } = useViewModel({
    brandUnselectable: brandFilterMode === 'all',
  });

  const handleLink = (link: string) => {
    navigate({ pathname: link });
  };

  const isBrandButtonDisabled = brandFilterMode === 'disabled';

  const isHidden =
    isPiracy &&
    user?.role &&
    ![MarqCommonEnumsClientClientRoleEnum.Admin, MarqCommonEnumsClientClientRoleEnum.Owner].includes(user.role);
  const settingLink = isPiracy
    ? `/${RootRouteEnum.Settings}/${SettingsRouteEnum.People}`
    : `/${RootRouteEnum.Settings}`;

  return (
    <MDSGNB {...restProps}>
      <div className={classes.container}>
        {brand.isSuccess && !isPiracy && (
          <>
            <MDSDivider orientation="vertical" length={16} />
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {client.logoUrl && (
                <MDSImage
                  src={client.logoUrl}
                  width="28px"
                  height="28px"
                  iconSize="x-small"
                  borderColor="color/border/neutral/default/normal"
                />
              )}
              {brand.list.length < 2 ? (
                <MDSTypography weight="medium">{brand.selectedName}</MDSTypography>
              ) : isBrandButtonDisabled ? (
                <MDSPlainButton color="bluegray" endIcon={<MDSIcon.ArrowDown variant="outline" />} isDisabled>
                  {brand.selectedName}
                </MDSPlainButton>
              ) : brand.unselectable ? (
                <MDSTooltip title="This page can't be viewed by brand" position="bottom-center">
                  <MDSPlainButton color="bluegray" endIcon={<MDSIcon.ArrowDown variant="outline" />} isDisabled>
                    {client.name || ALL_BRANDS_LABEL}
                  </MDSPlainButton>
                </MDSTooltip>
              ) : (
                <MDSDropdown
                  key={`selected-brand-${brand.selectedId}`}
                  renderAnchor={() => (
                    <MDSPlainButton color="bluegray" endIcon={<MDSIcon.ArrowDown variant="outline" />}>
                      {brand.selectedName}
                    </MDSPlainButton>
                  )}
                  value={brand.selectedId}
                  list={brand.options}
                  onChange={(value) => brand.onChange(value as number)}
                />
              )}
            </div>
          </>
        )}
        <div className={classes.utilityContainer}>
          {country.isAvailableAll
            ? null // 선택할 수 있는 모든 국가가 선택되어있는 경우에는 따로 표시하지 않는다.
            : country.options &&
              (country.options.length === 1 ? (
                <div className={classes.countryBox}>
                  <div>{getFlagEmoji(country.options[0].value as CountryCodeEnum)}</div>
                  <MDSTypography variant="body" size="m" weight="medium">
                    {country.options[0].label}
                  </MDSTypography>
                </div>
              ) : country.options.length > 1 ? (
                <div className={classes.countryBox}>
                  <MDSTypography variant="body" size="m" weight="medium" as="div">
                    Access to
                  </MDSTypography>
                  <MDSTooltip title={country.options.map((c) => c.label).join(', ')} position="bottom-center">
                    <MDSTypography
                      variant="body"
                      size="m"
                      weight="medium"
                      as="div"
                      className={classes.countryCountLabel}
                    >
                      {country.options.length} countries
                    </MDSTypography>
                  </MDSTooltip>
                </div>
              ) : null)}

          <ReleaseNote />
          {!isHidden && (
            <MDSTooltip title="Settings" size="small" position="bottom-center" style={{ marginTop: '4px' }}>
              <MDSPlainButton
                color="bluegray"
                onClick={() => handleLink(settingLink)}
                icon={<MDSIcon.Settings variant="fill" />}
              />
            </MDSTooltip>
          )}
          <MDSDropdown
            list={[{ label: 'Sign out', onClick: user.onLogout }]}
            renderAnchor={() => (
              <MDSPlainButton
                color="bluegray"
                isDisabled={
                  isAdmin // 왜 admin에서는 이 기능을 사용하지 않게 했는지는 PROD-13225 티켓 참고
                }
                icon={<MDSIcon.AccountProfile variant="fill" />}
              />
            )}
          />
        </div>
      </div>
    </MDSGNB>
  );
};
